import React, { useState, useEffect, useCallback } from 'react';
import {
  MdStorage, MdAdd, MdDelete, MdFileDownload, MdPictureAsPdf,
  MdPlayArrow, MdCheckCircle, MdHelpOutline, MdSave, MdClose,
} from 'react-icons/md';
import api from '../api/axios';
import { useChangeStream } from '../lib/useChangeStream';
import IdentifierBadges from '../components/IdentifierBadges';
import { downloadFromApi } from '../lib/fileDownload';
import { tsFilename } from '../lib/filename';
import { confirm } from '../lib/dialogs';
import SearchableSelect from '../components/SearchableSelect';

// Map raw_inventory.tag_type → label used by IdentifierBadges. Pre-feature
// rows have NULL tag_type, which we render as EPC (the default for the
// historical UHF-only path).
const scanTypeLabel = (t) => {
  switch ((t || 'epc').toLowerCase()) {
    case 'tid':     return 'TID';
    case 'barcode': return 'BAR';
    case 'qr':      return 'QR';
    case 'nfc':     return 'NFC';
    case 'epc':
    default:        return 'EPC';
  }
};

const Toast = ({ msg, type, onClose }) => (
  <div style={{
    position: 'fixed', top: 20, right: 20, zIndex: 9999,
    padding: '12px 18px', borderRadius: 8, color: 'white',
    background: type === 'error' ? '#d32f2f' : type === 'warning' ? '#f57c00' : '#2e7d32',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  }}>
    {msg}
    <button onClick={onClose} style={{ marginLeft: 12, background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>×</button>
  </div>
);

const RawInventory = () => {
  const [scans, setScans] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [manualTag, setManualTag] = useState('');
  const [filterSession, setFilterSession] = useState('');
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');
  const [onlyUnknown, setOnlyUnknown] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [promoteModal, setPromoteModal] = useState(null);
  const [namePromptOpen, setNamePromptOpen] = useState(false);
  const [pendingName, setPendingName] = useState('');
  const [renameTarget, setRenameTarget] = useState(null); // { session_id, name }
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchScans = useCallback(async () => {
    const params = {};
    if (filterSession) params.session_id = filterSession;
    if (filterStart) params.start_date = filterStart;
    if (filterEnd) params.end_date = filterEnd;
    if (onlyUnknown) params.only_unknown = 'true';
    const res = await api.get('/raw-inventory', { params });
    setScans(res.data);
  }, [filterSession, filterStart, filterEnd, onlyUnknown]);

  const [sessionsError, setSessionsError] = useState(null);
  const fetchSessions = useCallback(async () => {
    try {
      const r = await api.get('/raw-inventory/sessions');
      setSessions(r.data);
      setSessionsError(null);
    } catch (err) {
      setSessionsError(err.response?.data?.error || err.message || 'Failed to load sessions');
    }
  }, []);

  useEffect(() => { fetchScans(); fetchSessions(); }, [fetchScans, fetchSessions]);
  useChangeStream(() => { fetchScans(); fetchSessions(); }, ['raw-inventory']);
  useEffect(() => {
    api.get('/locations').then(r => setLocations(r.data)).catch(() => {});
    api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
  }, []);

  const handleStartSession = async () => {
    try {
      const res = await api.post('/raw-inventory/session/start', {
        name: pendingName.trim() || null,
      });
      setActiveSession(res.data.session_id);
      setNamePromptOpen(false);
      setPendingName('');
      fetchSessions();
      showToast(pendingName.trim() ? `Session "${pendingName.trim()}" started` : 'Session started');
    } catch { showToast('Failed to start session', 'error'); }
  };

  const handleRenameSession = async () => {
    if (!renameTarget?.session_id || !renameTarget.name?.trim()) return;
    try {
      await api.patch(`/raw-inventory/session/${renameTarget.session_id}`, {
        name: renameTarget.name.trim(),
      });
      setRenameTarget(null);
      fetchSessions();
      showToast('Session renamed');
    } catch { showToast('Rename failed', 'error'); }
  };

  const handleDeleteScan = async (scanId) => {
    if (!(await confirm('Delete this scan? This cannot be undone.'))) return;
    try {
      await api.delete(`/raw-inventory/scan/${scanId}`);
      fetchScans();
      fetchSessions();
      showToast('Scan deleted');
    } catch (err) {
      showToast(err.response?.data?.error || 'Delete failed', 'error');
    }
  };

  const handleAddTag = async () => {
    if (!manualTag.trim()) return;
    if (!activeSession) { showToast('Start a session first', 'error'); return; }
    try {
      await api.post('/raw-inventory/scan', {
        tag_number: manualTag.trim(),
        rssi: -(Math.floor(Math.random() * 40) + 50),
        session_id: activeSession,
      });
      setManualTag('');
      fetchScans(); fetchSessions();
      showToast('Tag added');
    } catch { showToast('Failed to add tag', 'error'); }
  };

  const handleBulkScan = async () => {
    if (!activeSession) { showToast('Start a session first', 'error'); return; }
    setBulkLoading(true);
    const count = Math.floor(Math.random() * 8) + 5;
    for (let i = 0; i < count; i++) {
      const tag = `E200-${String(Math.floor(Math.random() * 9999)).padStart(4,'0')}-${String(Math.floor(Math.random() * 9999)).padStart(4,'0')}`;
      const rssi = -(Math.floor(Math.random() * 40) + 50);
      try {
        await api.post('/raw-inventory/scan', { tag_number: tag, rssi, session_id: activeSession });
        await new Promise(r => setTimeout(r, 100));
      } catch {}
    }
    setBulkLoading(false);
    fetchScans(); fetchSessions();
    showToast(`Added ${count} simulated scans`);
  };

  const handleClearSession = async (sessionId) => {
    // Null session_id (the "(no session)" group) needs a sentinel in the URL
    const isNull = sessionId == null;
    const urlSegment = isNull ? '_null' : encodeURIComponent(sessionId);
    if (!(await confirm(isNull
      ? 'Clear all scans that have no session? This is usually data from old or pre-session-aware tooling.'
      : 'Clear all scans for this session?'
    ))) return;
    try {
      const res = await api.delete(`/raw-inventory/session/${urlSegment}`);
      if (!isNull) {
        if (activeSession === sessionId) setActiveSession(null);
        if (filterSession === sessionId) setFilterSession('');
      }
      // Refresh sequentially so the UI definitely shows the post-delete state
      await fetchSessions();
      await fetchScans();
      showToast(`Cleared ${res.data?.deleted ?? 0} scan(s)`);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to clear session', 'error');
    }
  };

  const handleExport = async (type = 'excel') => {
    try {
      const params = filterSession ? `?session_id=${filterSession}` : '';
      const ext = type === 'excel' ? 'xlsx' : 'pdf';
      await downloadFromApi(`/raw-inventory/export/${type}${params}`, tsFilename('raw-inventory', ext));
    } catch (err) {
      showToast(err.message || 'Export failed', 'error');
    }
  };

  const handlePromote = async () => {
    if (!promoteModal?.asset_name?.trim() || !promoteModal?.serial_number?.trim()) {
      showToast('Asset name and serial number are required', 'error');
      return;
    }
    try {
      const r = await api.post(`/raw-inventory/${promoteModal.id}/promote`, {
        asset_name: promoteModal.asset_name.trim(),
        serial_number: promoteModal.serial_number.trim(),
        asset_tag: promoteModal.asset_tag || null,
        barcode: promoteModal.barcode || null,
        category: promoteModal.category || null,
        location_id: promoteModal.location_id || null,
        notes: promoteModal.notes || null,
      });
      showToast(`Asset registered: ${r.data.asset.asset_name}`);
      setPromoteModal(null);
      fetchScans(); fetchSessions();
    } catch (err) {
      showToast(err.response?.data?.error || 'Promote failed', 'error');
    }
  };

  const getRssiColor = (rssi) => {
    if (rssi == null) return 'var(--text-muted)';
    if (rssi > -60) return 'var(--success)';
    if (rssi > -75) return 'var(--warning)';
    return 'var(--danger)';
  };

  return (
    <div className="page-container">
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div className="page-header">
        <div>
          <div className="page-title"><MdStorage /> Raw Inventory</div>
          <div className="page-subtitle">
            Tags pushed by readers and handhelds. Unknown tags can be promoted to registered assets.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-success btn-sm" onClick={() => handleExport('excel')}><MdFileDownload /> Excel</button>
          <button className="btn btn-danger btn-sm" onClick={() => handleExport('pdf')}><MdPictureAsPdf /> PDF</button>
        </div>
      </div>

      {/* Session list / cards */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <div className="card-title">Sessions</div>
          {!activeSession && (
            <button className="btn btn-primary btn-sm" onClick={() => { setPendingName(''); setNamePromptOpen(true); }}>
              <MdPlayArrow /> Start New Session
            </button>
          )}
        </div>
        {activeSession && (() => {
          const meta = sessions.find(s => s.session_id === activeSession);
          return (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: 10, marginBottom: 12,
            background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 8,
          }}>
            <span style={{ fontWeight: 700, color: 'var(--success)' }}>● ACTIVE</span>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {meta?.name && <strong style={{ fontSize: 13 }}>{meta.name}</strong>}
              <code style={{ fontSize: 11, color: '#555' }}>{activeSession}</code>
            </div>
            <input
              className="form-control"
              value={manualTag}
              onChange={e => setManualTag(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddTag()}
              placeholder="Type a tag and press Enter…"
              style={{ flex: 1, maxWidth: 280 }}
            />
            <button className="btn btn-accent btn-sm" onClick={handleAddTag} disabled={!manualTag.trim()}>
              <MdAdd /> Add
            </button>
            <button className="btn btn-warning btn-sm" onClick={handleBulkScan} disabled={bulkLoading}>
              {bulkLoading ? '…' : <><MdPlayArrow /> Bulk Simulate</>}
            </button>
            <button className="btn btn-outline btn-sm" onClick={() => setActiveSession(null)}>Stop using</button>
          </div>
          );
        })()}

        {sessionsError ? (
          <div style={{
            padding: 16, textAlign: 'center', fontSize: 13,
            color: '#d32f2f', background: '#ffebee', border: '1px solid #ffcdd2', borderRadius: 8,
          }}>
            <strong>Couldn't load sessions:</strong> {sessionsError}
            <button
              className="btn btn-outline btn-sm"
              style={{ marginLeft: 12 }}
              onClick={fetchSessions}
            >Retry</button>
          </div>
        ) : sessions.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#888', fontSize: 13 }}>
            No sessions yet. Start one to begin recording scans.
          </div>
        ) : (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 10,
          }}>
            {sessions.map(s => {
              const sid = s.session_id;
              const isNull = sid == null;
              const isFilter = !isNull && filterSession === sid;
              const isActive = !isNull && activeSession === sid;
              return (
                <div key={sid || '__null'} style={{
                  padding: 10, borderRadius: 8,
                  border: `1px solid ${isFilter ? '#1565c0' : isNull ? '#cbd2de' : '#e0e4eb'}`,
                  background: isFilter ? '#e3f2fd' : isNull ? '#fafbfc' : 'white',
                  cursor: isNull ? 'default' : 'pointer',
                }} onClick={() => { if (!isNull) setFilterSession(isFilter ? '' : sid); }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    {isNull ? (
                      <span style={{ fontSize: 11, color: '#999', fontStyle: 'italic' }}>(no session)</span>
                    ) : s.name ? (
                      <div>
                        <strong style={{ fontSize: 13, color: '#1a3c6e' }}>{s.name}</strong>
                        <code style={{ fontSize: 10, color: '#888', marginLeft: 6 }}>{sid.slice(0, 8)}…</code>
                      </div>
                    ) : (
                      <code style={{ fontSize: 11, color: '#666' }}>{sid.slice(0, 18)}…</code>
                    )}
                    {isActive && <span className="badge badge-success" style={{ fontSize: 9 }}>ACTIVE</span>}
                  </div>
                  <div style={{ fontSize: 12, color: '#444', marginTop: 6 }}>
                    {s.scan_count} scans · {s.unique_tags} unique tags
                  </div>
                  <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                    {s.matched_assets > 0 && <span style={{ color: '#2e7d32' }}>✓ {s.matched_assets} matched</span>}
                    {s.unmatched_scans > 0 && <span style={{ color: '#ef6c00', marginLeft: 8 }}>? {s.unmatched_scans} unmatched</span>}
                  </div>
                  <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                    {s.first_scan_at && new Date(s.first_scan_at).toLocaleString()}
                  </div>
                  <div style={{ display: 'flex', gap: 4, marginTop: 8 }} onClick={e => e.stopPropagation()}>
                    {!isActive && !isNull && (
                      <button className="btn btn-outline btn-sm" onClick={() => setActiveSession(sid)}>Use</button>
                    )}
                    {!isNull && (
                      <button
                        className="btn btn-outline btn-sm"
                        title={s.name ? 'Rename session' : 'Name this session'}
                        onClick={() => setRenameTarget({ session_id: sid, name: s.name || '' })}
                      >Rename</button>
                    )}
                    <button className="btn btn-danger btn-icon btn-sm"
                      title={isNull ? 'Delete all scans without a session' : 'Delete this session and all its scans'}
                      onClick={() => handleClearSession(sid)}><MdDelete /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="filters-row" style={{ marginBottom: 0 }}>
          <div style={{ flex: 1 }}>
            <label className="form-label">Session filter</label>
            <SearchableSelect className="form-control" value={filterSession} onChange={e => setFilterSession(e.target.value)}>
              <option value="">All sessions</option>
              {sessions.filter(s => s.session_id).map(s => (
                <option key={s.session_id} value={s.session_id}>
                  {s.name ? `${s.name} · ${s.scan_count} scans` : `${s.session_id.slice(0, 16)}… · ${s.scan_count} scans`}
                </option>
              ))}
            </SearchableSelect>
          </div>
          <div>
            <label className="form-label">Start</label>
            <input type="date" className="form-control" value={filterStart} onChange={e => setFilterStart(e.target.value)} />
          </div>
          <div>
            <label className="form-label">End</label>
            <input type="date" className="form-control" value={filterEnd} onChange={e => setFilterEnd(e.target.value)} />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
            {/* Toggle pill instead of a checkbox-in-label — the previous
                version had an unstyled native checkbox glued onto a coloured
                box and looked broken next to the rest of the filters. */}
            <button
              type="button"
              onClick={() => setOnlyUnknown(v => !v)}
              title="Only unknown tags"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                height: 38, padding: '0 14px',
                fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
                borderRadius: 8, cursor: 'pointer',
                border: `1.5px solid ${onlyUnknown ? '#ef6c00' : 'var(--border)'}`,
                background: onlyUnknown ? '#fff3e0' : 'white',
                color: onlyUnknown ? '#ef6c00' : 'var(--text-secondary)',
                transition: 'all 120ms ease',
              }}
            >
              {onlyUnknown ? '● Unknown only' : '○ Unknown only'}
            </button>
            <button className="btn btn-outline btn-sm" onClick={() => {
              setFilterSession(''); setFilterStart(''); setFilterEnd(''); setOnlyUnknown(false);
            }}>Clear</button>
          </div>
        </div>
      </div>

      {/* Scans table */}
      <div className="card">
        <div className="card-header">
          <div className="card-title"><MdStorage style={{ color: 'var(--accent)' }} /> Scan Records</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="badge badge-info">{scans.length} records</span>
            {/* Clear only operates on the FILTERED session. Without a session
                selected the button is disabled — wiping every session's scans
                in one click was too dangerous. To delete a session entirely
                use the trash icon on its card. */}
            <button
              className="btn btn-outline btn-sm"
              disabled={!filterSession || scans.length === 0}
              title={
                !filterSession
                  ? 'Click a session card first to scope the clear.'
                  : `Clear scan rows for the selected session. The session card stays.`
              }
              onClick={async () => {
                if (!filterSession) return;
                const meta = sessions.find(s => s.session_id === filterSession);
                const label = meta?.name || filterSession.slice(0, 12);
                if (!(await confirm(`Clear scan records for "${label}"? The session itself is kept.`))) return;
                try {
                  const r = await api.delete(`/raw-inventory/scans?session_id=${encodeURIComponent(filterSession)}`);
                  showToast(r.data.message || 'Scans cleared');
                  fetchScans();
                  fetchSessions();
                } catch (err) {
                  showToast(err.response?.data?.error || 'Clear failed', 'error');
                }
              }}
            >
              <MdDelete /> Clear Records
            </button>
          </div>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Type</th>
                <th>Tag Number</th>
                <th>Match</th>
                <th>Identifiers</th>
                <th>Count</th>
                <th>RSSI (dBm)</th>
                <th>Session</th>
                <th>Scanned By</th>
                <th>When</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {scans.length === 0 ? (
                <tr><td colSpan={11} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>
                  No scan records.
                </td></tr>
              ) : scans.map((scan, i) => (
                <tr key={scan.id}>
                  <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{i + 1}</td>
                  <td>
                    <IdentifierBadges
                      identifiers={[scanTypeLabel(scan.tag_type)]}
                      highlight={scanTypeLabel(scan.tag_type)}
                    />
                  </td>
                  <td><code style={{ fontSize: 12, color: 'var(--accent)' }}>{scan.tag_number}</code></td>
                  <td>
                    {scan.matched_asset_id ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <MdCheckCircle style={{ color: 'var(--success)', fontSize: 14 }} />
                        <span style={{ fontSize: 12, fontWeight: 500 }}>{scan.matched_asset_name}</span>
                      </span>
                    ) : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#ef6c00' }}>
                        <MdHelpOutline style={{ fontSize: 14 }} />
                        <span style={{ fontSize: 12, fontStyle: 'italic' }}>Unknown</span>
                      </span>
                    )}
                  </td>
                  <td>
                    <IdentifierBadges
                      identifiers={scan.matched_asset_identifiers || []}
                      highlight={scanTypeLabel(scan.tag_type)}
                    />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span
                      title="Number of times this tag was scanned in this session"
                      style={{
                        display: 'inline-block',
                        minWidth: 28,
                        padding: '2px 8px',
                        borderRadius: 12,
                        fontSize: 11,
                        fontWeight: 700,
                        background: scan.scan_count > 1 ? 'var(--accent)' : '#eef2f7',
                        color: scan.scan_count > 1 ? 'white' : 'var(--text-secondary)',
                      }}
                    >
                      ×{scan.scan_count || 1}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontWeight: 600, color: getRssiColor(scan.rssi), fontFamily: 'monospace' }}>
                      {scan.rssi != null ? `${scan.rssi} dBm` : '—'}
                    </span>
                  </td>
                  <td>
                    {scan.session_id ? (() => {
                      const meta = sessions.find(s => s.session_id === scan.session_id);
                      return meta?.name
                        ? <span style={{ fontSize: 12 }}>{meta.name}</span>
                        : <code style={{ fontSize: 10, color: 'var(--text-muted)' }}>{scan.session_id.slice(0, 12)}…</code>;
                    })() : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
                  </td>
                  <td style={{ fontSize: 13 }}>{scan.scanned_by_name || '—'}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {new Date(scan.last_scanned_at || scan.timestamp).toLocaleString()}
                  </td>
                  <td>
                    <div className="action-buttons" style={{ display: 'flex', gap: 4 }}>
                      {!scan.matched_asset_id && (
                        <button className="btn btn-success btn-sm" title="Promote to registered asset"
                          onClick={() => setPromoteModal({
                            id: scan.id,
                            tag_number: scan.tag_number,
                            asset_name: '', serial_number: '',
                            asset_tag: '', barcode: '', category: '',
                            location_id: '', notes: '',
                          })}>
                          <MdAdd /> Register
                        </button>
                      )}
                      <button className="btn btn-danger btn-icon btn-sm"
                        title="Delete this scan"
                        onClick={() => handleDeleteScan(scan.id)}><MdDelete /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Name session prompt */}
      {namePromptOpen && (
        <div className="modal-overlay" onClick={() => setNamePromptOpen(false)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title"><MdPlayArrow /> Start scan session</div>
              <button className="btn btn-icon" onClick={() => setNamePromptOpen(false)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Session name</label>
                <input className="form-control" autoFocus
                  placeholder="e.g. Server room sweep, Floor 2 audit…"
                  value={pendingName}
                  onChange={e => setPendingName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleStartSession()} />
                <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                  Optional. Helps identify this batch in the session list later. You can leave it blank.
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setNamePromptOpen(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleStartSession}>
                <MdPlayArrow /> Start
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename session prompt */}
      {renameTarget && (
        <div className="modal-overlay" onClick={() => setRenameTarget(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Rename session</div>
              <button className="btn btn-icon" onClick={() => setRenameTarget(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Session name</label>
                <input className="form-control" autoFocus
                  value={renameTarget.name}
                  onChange={e => setRenameTarget(t => ({ ...t, name: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && handleRenameSession()} />
                <code style={{ fontSize: 11, color: '#888', marginTop: 4, display: 'block' }}>
                  {renameTarget.session_id}
                </code>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setRenameTarget(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleRenameSession} disabled={!renameTarget.name?.trim()}>
                <MdSave /> Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Promote modal */}
      {promoteModal && (
        <div className="modal-overlay" onClick={() => setPromoteModal(null)}>
          <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title"><MdAdd /> Promote tag to asset</div>
              <button className="btn btn-icon" onClick={() => setPromoteModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: 8, fontSize: 12, color: '#7a5400', marginBottom: 12 }}>
                <strong>RFID tag:</strong> <code style={{ color: 'var(--accent)' }}>{promoteModal.tag_number}</code>
                <br/>This will create a new active asset using this tag.
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Asset Name *</label>
                  <input className="form-control" value={promoteModal.asset_name}
                    onChange={e => setPromoteModal(m => ({ ...m, asset_name: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Serial Number *</label>
                  <input className="form-control" value={promoteModal.serial_number}
                    onChange={e => setPromoteModal(m => ({ ...m, serial_number: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Asset Tag</label>
                  <input className="form-control" value={promoteModal.asset_tag}
                    onChange={e => setPromoteModal(m => ({ ...m, asset_tag: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Barcode</label>
                  <input className="form-control" value={promoteModal.barcode}
                    onChange={e => setPromoteModal(m => ({ ...m, barcode: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <SearchableSelect className="form-control" value={promoteModal.category}
                    onChange={e => setPromoteModal(m => ({ ...m, category: e.target.value }))}>
                    <option value="">— None —</option>
                    {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </SearchableSelect>
                </div>
                <div className="form-group">
                  <label className="form-label">Location</label>
                  <SearchableSelect className="form-control" value={promoteModal.location_id}
                    onChange={e => setPromoteModal(m => ({ ...m, location_id: e.target.value }))}>
                    <option value="">— Unassigned —</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.location_code} · {l.location_name}</option>)}
                  </SearchableSelect>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={2} value={promoteModal.notes}
                  onChange={e => setPromoteModal(m => ({ ...m, notes: e.target.value }))} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setPromoteModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handlePromote}><MdSave /> Register Asset</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RawInventory;
